import { ref } from "vue";
import type {
    IConfig,
    IRepository,
} from "@/wc/registry/lib/service/repository/types";
import { SupportedConfigTypes } from "@/wc/registry/lib/service/repository/types";
import lister from "@/wc/registry/lib/service/repository/index";
import * as parsers from "@/wc/registry/lib/service/repository/parsers.js";
import { checkRepositoryConfiguration } from "@/wc/registry/lib/service/repository/finder";
import rolesRepository from "@/lib/repository/custom/roles";
import productsRepository from "@/lib/repository/custom/products";
import businessEntitiesRepository from "@/lib/repository/custom/businessEntities";
import { objects as updateBusinessEntityObjects } from "@/lib/repository/modifications/domainBusinessEntityMgtUpdateBusinessEntity";
import { objects as getBusinessEntityByDomainObjects } from "@/lib/repository/modifications/domainBusinessEntityMgtGetBusinessEntitiesByDomain";
import { objects as createBusinessEntityObjects } from "@/lib/repository/modifications/domainBusinessEntityMgtCreateBusinessEntity";
import { objects as getBusinessEntityObjects } from "@/lib/repository/modifications/domainBusinessEntityMgtGetBusinessEntity";
import config from "@/config";

const repositoriesAndTheirConfigs: {
    [repositoryName: string]: IConfig & { waitForOtherRepositories?: string[] };
} = {
    domainRoleMgtGetRoles: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/domain/role_mgt/get_roles/start"],
        openApiFileLocation: config.openApiFileLocation,
    },
    domainRoleMgtCreateRole: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/domain/role_mgt/create_role/start"],
        openApiFileLocation: config.openApiFileLocation,
    },
    domainRoleMgtDeleteRole: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/domain/role_mgt/delete_role/start"],
        openApiFileLocation: config.openApiFileLocation,
    },
    roles: {
        type: SupportedConfigTypes.Custom,
        customRepositoryClass: rolesRepository,
        waitForOtherRepositories: [
            "domainRoleMgtGetRoles",
            "domainRoleMgtCreateRole",
            "domainRoleMgtDeleteRole",
        ],
    },
    productProductMgtGetProductsByDomain: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/product/product_mgt/get_products_by_domain/start"],
        openApiFileLocation: config.openApiFileLocation,
    },
    products: {
        type: SupportedConfigTypes.Custom,
        customRepositoryClass: productsRepository,
        waitForOtherRepositories: ["productProductMgtGetProductsByDomain"],
    },
    domainBusinessEntityMgtGetBusinessEntitiesByDomain: {
        type: SupportedConfigTypes.OpenApi,
        paths: [
            "/domain/business_entity_mgt/get_business_entities_by_domain/start",
        ],
        modifications: {
            objects: {
                create: getBusinessEntityByDomainObjects.create,
            },
        },
        openApiFileLocation: config.openApiFileLocation,
    },
    domainBusinessEntityMgtCreateBusinessEntity: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/domain/business_entity_mgt/create_business_entity/start"],
        modifications: {
            objects: {
                create: createBusinessEntityObjects.create,
            },
        },
        openApiFileLocation: config.openApiFileLocation,
    },
    domainBusinessEntityMgtGetBusinessEntity: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/domain/business_entity_mgt/get_business_entity/start"],
        modifications: {
            objects: {
                create: getBusinessEntityObjects.create,
            },
        },
        openApiFileLocation: config.openApiFileLocation,
    },
    domainBusinessEntityMgtUpdateBusinessEntity: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/domain/business_entity_mgt/update_business_entity/start"],
        modifications: {
            objects: {
                create: updateBusinessEntityObjects.create,
            },
        },
        openApiFileLocation: config.openApiFileLocation,
    },
    domainBusinessEntityMgtDeleteBusinessEntity: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/domain/business_entity_mgt/delete_business_entity/start"],
        openApiFileLocation: config.openApiFileLocation,
    },
    businessEntities: {
        type: SupportedConfigTypes.Custom,
        customRepositoryClass: businessEntitiesRepository,
        waitForOtherRepositories: [
            "domainBusinessEntityMgtGetBusinessEntitiesByDomain",
            "domainBusinessEntityMgtCreateBusinessEntity",
            "domainBusinessEntityMgtGetBusinessEntity",
            "domainBusinessEntityMgtUpdateBusinessEntity",
            "domainBusinessEntityMgtDeleteBusinessEntity",
        ],
    },
    userSelf: {
        type: SupportedConfigTypes.OpenApi,
        paths: ["/user/me"],
        openApiFileLocation: config.openApiFileLocation,
    },
    "users-make-user": {
        type: SupportedConfigTypes.OpenApi,
        "paths": ["/user/{user_id}/make-user"],
        openApiFileLocation: config.openApiFileLocation,
    },
    "users-make-user-manager": {
        type: SupportedConfigTypes.OpenApi,
        "paths": ["/user/{user_id}/make-user-manager"],
        openApiFileLocation: config.openApiFileLocation,
    },
    userDomainRoles: {
        type: SupportedConfigTypes.OpenApi,
        "paths": ["/user/{user_id}/domain-roles"],
        openApiFileLocation: config.openApiFileLocation,
    },
    users: {
        type: SupportedConfigTypes.OpenApi,
        "paths": ["/user/", "/user/{user_id}"],
        waitForOtherRepositories: [
            "userSelf",
            "users-make-user",
            "users-make-user-manager",
            "userDomainRoles",
        ],
        openApiFileLocation: config.openApiFileLocation,
    },
};
/**
 * @deprecated Don not use useRepository, use the generated client instead.
 */
export function useRepository(repositoryName: string) {
    let _resolve = null;
    const isRegisteredPromise = new Promise((resolve) => {
        _resolve = resolve;
    });
    const registered = ref(false);
    const repository = ref<IRepository>();

    if (!(repositoryName in repositoriesAndTheirConfigs)) {
        throw new Error(
            `The repository '${repositoryName}' is not configured.`
        );
    }
    const config = repositoriesAndTheirConfigs[repositoryName];
    repository.value = lister.get(repositoryName);

    const configureRepository = async (
        repositoryName: string,
        config: IConfig
    ) => {
        if ("waitForOtherRepositories" in config) {
            await Promise.all(
                config.waitForOtherRepositories.map(
                    (repositoryName) =>
                        useRepository(repositoryName).isRegisteredPromise
                )
            );
        }

        if (!config.type) {
            throw new Error(
                `The repository '${repositoryName}' must have a type defined in its configuration.`
            );
        }
        checkRepositoryConfiguration(repositoryName, config.type, config);

        let repo;
        if (config.type === SupportedConfigTypes.Custom) {
            if (!config.customRepositoryClass) {
                throw new Error(
                    `The repository '${repositoryName}' must have a customRepositoryClass defined in its configuration.`
                );
            }

            repo = new config.customRepositoryClass();

            if ("initialized" in repo) {
                await repo.initialized;
            }
        } else if (config.type === SupportedConfigTypes.OpenApi) {
            if (!config.openApiFileLocation) {
                throw new Error(
                    `The repository '${repositoryName}' must have an openApiFileLocation defined in its configuration.`
                );
            }

            if (!config.paths) {
                throw new Error(
                    `The repository '${repositoryName}' must have paths defined in its configuration.`
                );
            }

            repo = await parsers.createRepositoryFromOpenApiSpec(
                repositoryName,
                config.openApiFileLocation,
                config.paths
            );
        }

        if (!repo) {
            throw new Error(
                `The repository '${repositoryName}' could not be created.`
            );
        }

        if (config.modifications?.objects) {
            lister.finder.modifyRepositoryObjects(
                repo.objects,
                config.modifications.objects
            );
        }

        if (config.modifications?.properties) {
            lister.finder.modifyRepositoryProperties(
                repo.properties,
                config.modifications.properties
            );
        }

        if (config.modifications?.ordering) {
            lister.finder.modifyRepositoryActionPaths(
                repo.actionPaths,
                config.modifications,
                repositoryName
            );
        }

        lister.finder.modifyRepositoryManagerToInjectParameters(repo.objects);

        lister.finder.addRepository(repositoryName, repo as IRepository);

        repository.value = lister.get(repositoryName);
        registered.value = true;
        _resolve();
    };

    if (!repository.value) {
        configureRepository(repositoryName, config);
    } else {
        registered.value = true;
        _resolve();
    }

    return {
        isRegistered: registered,
        isRegisteredPromise,
        repository,
    };
}
